import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShoppingCart, Search, Package, ArrowLeft, User } from "lucide-react";
import ProductDetailModal from "@/components/ProductDetailModal";

const LOGO = "https://customer-assets.emergentagent.com/job_0890dc80-6146-4c2d-b11f-fa0b2de6d70b/artifacts/ekzdqal8_image.png";
const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: "all", label: "Semua" },
  { value: "styling", label: "Styling" },
  { value: "perawatan", label: "Perawatan" },
  { value: "grooming", label: "Grooming" },
];

export default function StorePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [cartCount, setCartCount] = useState(0);
  const [detail, setDetail] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get("/products").then(r => setProducts(r.data)).catch(() => {});
    if (user) api.get("/cart").then(r => setCartCount(r.data?.items?.length || 0)).catch(() => {});
  }, [user]);

  // Auto-open detail when ?buy=ID is set (e.g., from landing page modal)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const buyId = searchParams.get("buy");
    if (buyId && products.length > 0) {
      const p = products.find(x => x.id === buyId);
      if (p) setDetail(p);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, searchParams]);

  const addToCart = async (product) => {
    if (!user) { navigate("/login"); toast.info("Silakan login terlebih dahulu"); return; }
    try {
      const { data } = await api.post("/cart", { product_id: product.id, qty: 1 });
      setCartCount(data.items?.length || 0);
      toast.success(`${product.name} ditambahkan ke keranjang`);
    } catch (err) { toast.error("Gagal menambahkan ke keranjang"); }
  };

  const buyNow = async (product) => {
    if (!user) { navigate("/login"); toast.info("Silakan login terlebih dahulu"); return; }
    setBuying(true);
    try {
      const { data } = await api.post("/cart", { product_id: product.id, qty: 1 });
      setCartCount(data.items?.length || 0);
      setDetail(null);
      toast.success("Membuka keranjang...");
      navigate("/keranjang");
    } catch (err) {
      toast.error("Gagal memproses pembelian");
    } finally {
      setBuying(false);
    }
  };

  const getImgUrl = (url) => url && url.startsWith("fi-barbershop") ? `${API_BASE}/api/files/${url}` : url;

  const filtered = products.filter(p => {
    if (filter !== "all" && p.category !== filter) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navbar */}
      <nav className="fixed w-full z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#262626]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="store-home-link"><img src={LOGO} alt="FI" className="w-8 h-8 rounded-full" /></Link>
            <span className="font-heading text-sm font-bold uppercase tracking-wider">Toko Online</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-gray-400 hover:text-white flex items-center gap-1" data-testid="store-back"><ArrowLeft size={14} /> Beranda</Link>
            {user && (
              <Link to="/keranjang" className="relative" data-testid="cart-link">
                <ShoppingCart size={20} className="text-gray-400 hover:text-white" />
                {cartCount > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#E60000] text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{cartCount}</span>}
              </Link>
            )}
            {user && user.role === "customer" && (
              <Link to="/portal" data-testid="portal-link"><User size={18} className="text-gray-400 hover:text-white" /></Link>
            )}
            {!user && <Link to="/login" data-testid="store-login"><Button className="bg-[#E60000] hover:bg-[#FF1A1A] text-white h-8 rounded-none text-xs font-bold uppercase">Masuk</Button></Link>}
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-16 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-2">TOKO ONLINE</p>
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="store-title">Produk Barbershop</h1>
          <p className="text-gray-400 mt-2">Produk perawatan rambut & grooming premium</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8" data-testid="store-filters">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input className="bg-[#0F0F0F] border-[#262626] text-white h-10 rounded-none pl-9" placeholder="Cari produk..." value={search} onChange={e => setSearch(e.target.value)} data-testid="store-search" />
          </div>
          <div className="flex gap-2">
            {categories.map(c => (
              <button key={c.value} onClick={() => setFilter(c.value)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${filter === c.value ? "border-[#E60000] bg-[#E60000]/10 text-[#E60000]" : "border-[#262626] text-gray-400 hover:border-[#E60000]"}`} data-testid={`filter-${c.value}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="product-grid">
          {filtered.map(p => (
            <div key={p.id} className="bg-[#0F0F0F] border border-[#262626] hover:border-[#E60000] transition-all duration-300 group cursor-pointer" data-testid={`product-card-${p.id}`} onClick={() => setDetail(p)}>
              <div className="aspect-square bg-[#1A1A1A] flex items-center justify-center overflow-hidden">
                {p.image_url ? (
                  <img src={getImgUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <Package size={48} className="text-gray-600" />
                )}
              </div>
              <div className="p-5">
                <Badge className="text-[9px] bg-[#262626] text-gray-400 mb-2">{p.category}</Badge>
                <h3 className="text-white font-semibold mb-1">{p.name}</h3>
                <p className="text-gray-500 text-xs mb-3 line-clamp-2">{p.description}</p>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-xl font-bold text-[#E60000]">{formatPrice(p.price)}</span>
                  <span className="text-[10px] text-gray-500">Stok: {p.stock}</span>
                </div>
                <Button onClick={(e) => { e.stopPropagation(); addToCart(p); }} className="w-full mt-3 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-9 text-xs" data-testid={`add-to-cart-${p.id}`} disabled={p.stock <= 0}>
                  {p.stock <= 0 ? "Habis" : "Tambah ke Keranjang"}
                </Button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-gray-500 col-span-full text-center py-12">Tidak ada produk ditemukan</p>}
        </div>
      </div>

      {/* Product Detail Modal */}
      <ProductDetailModal
        product={detail}
        onClose={() => setDetail(null)}
        onBuyNow={(p) => buyNow(p)}
        onAddCart={(p) => { addToCart(p); setDetail(null); }}
        buyDisabled={buying}
        buyLabel={buying ? "Memproses..." : "Beli Sekarang"}
      />
    </div>
  );
}
